import { useState, type FC } from 'react';
import type { StudentGroupChatMessage } from '../types';
import { MessageSquareIcon } from './CourseStudentIcons';

interface StudentChatModalProps {
  isOpen: boolean;
  groupCode: string;
  currentUserName?: string | null;
  messages: StudentGroupChatMessage[];
  onClose: () => void;
  onSendMessage: (text: string) => Promise<void>;
}

export const StudentChatModal: FC<StudentChatModalProps> = ({
  isOpen,
  groupCode,
  currentUserName,
  messages,
  onClose,
  onSendMessage,
}) => {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!text.trim() || isSending) return;
    const msg = text.trim();
    setText('');
    setIsSending(true);
    try {
      await onSendMessage(msg);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/40 bg-cyan-950/60 text-cyan-400">
              <MessageSquareIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Групповой чат · {groupCode}</h3>
              <p className="text-[11px] text-slate-400">
                Преподаватель, староста и обучающиеся академической группы
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <div className="max-h-80 overflow-y-auto space-y-2.5 pr-1 text-xs">
          {messages.map((m) => {
            const isMe = Boolean(currentUserName && m.sender_name === currentUserName);
            return (
              <div
                key={m.id}
                className={`p-3 rounded-xl ${
                  isMe
                    ? 'bg-cyan-950/60 border border-cyan-500/40 text-cyan-200 ml-8'
                    : 'bg-slate-900/80 border border-slate-800 text-slate-300 mr-8'
                }`}
              >
                <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
                  <span className="font-bold text-slate-300">
                    {m.sender_name} ({m.sender_role})
                  </span>
                  <span>{m.created_at || 'Сейчас'}</span>
                </div>
                <p className="leading-relaxed">{m.message}</p>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-800">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Написать сообщение в чат группы..."
            className="flex-1 rounded-xl border border-slate-700 bg-slate-900 px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending || !text.trim()}
            className="rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] px-4 py-2.5 text-xs font-bold text-slate-950 transition-all shadow-md disabled:opacity-50"
          >
            Отправить
          </button>
        </div>
      </div>
    </div>
  );
};
